import { apiAddress } from "../../config/server";
import {
  IAssociation,
  IAssociationConfig,
  IAssociationResource,
} from "../../types/association/Association";
import { ApiBase } from "./baseApi";

class AssociationsApi extends ApiBase {
  static associationsApiAddress = `${apiAddress}/api/association/`;

  private static buildEffectiveDateQuery(effectiveDate?: Date | string) {
    if (!effectiveDate) return '';
    const isoDate = effectiveDate instanceof Date ? effectiveDate.toISOString() : effectiveDate;
    return `?effectiveDate=${encodeURIComponent(isoDate)}`;
  }

  static getMyAssociations(effectiveDate?: Date | string): Promise<IAssociation[]> {
    const query = this.buildEffectiveDateQuery(effectiveDate);
    return AssociationsApi.get<IAssociation[]>(
      AssociationsApi.associationsApiAddress + query,
    ).then((res) => res.data.associations || res.data.data || []);
  }

  static getAdminAssociations(effectiveDate?: Date | string): Promise<IAssociation[]> {
    const query = this.buildEffectiveDateQuery(effectiveDate);
    return this.genericApiCall<IAssociation[]>(
      'GET',
      `${AssociationsApi.associationsApiAddress}admin${query}`,
      undefined,
      'Failed to fetch admin associations'
    ).catch(() => []); // Graceful fallback for non-admin users
  }

  static getAssociationById(associationId: string): Promise<IAssociation> {
    this.validateObjectId(associationId, 'association ID');

    return this.genericApiCall<IAssociation>(
      'GET',
      AssociationsApi.associationsApiAddress + associationId,
      undefined,
      'Failed to fetch association details'
    );
  }

  static getMyAssociationResources(): Promise<IAssociationResource[]> {
    return this.genericApiCall<IAssociationResource[]>(
      'GET',
      `${AssociationsApi.associationsApiAddress}self/resources`,
      undefined,
      'Failed to fetch user association resources'
    );
  }

  static updateAssociationConfig(associationId: string, configUpdates: Partial<IAssociationConfig>): Promise<IAssociation> {
    this.validateObjectId(associationId, 'association ID');

    // SECURITY: Validate config updates
    if (!configUpdates || typeof configUpdates !== 'object') {
      return Promise.reject(new Error('Invalid configuration updates'));
    }

    return this.genericApiCall<IAssociation>(
      'PATCH',
      `${AssociationsApi.associationsApiAddress}${associationId}/config`,
      configUpdates,
      'Failed to update association configuration'
    );
  }

  static getAssociationResources(associationId: string): Promise<IAssociationResource[]> {
    this.validateObjectId(associationId, 'association ID');

    return this.genericApiCall<IAssociationResource[]>(
      'GET',
      `${AssociationsApi.associationsApiAddress}${associationId}/resources`,
      undefined,
      'Failed to fetch association resources'
    );
  }

  static createAssociationResource(associationId: string, resource: {
    title: string;
    body: string;
    role?: string;
    activeFrom?: Date;
  }): Promise<IAssociationResource> {
    // SECURITY: Validate association ID format
    if (!associationId || typeof associationId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(associationId)) {
      return Promise.reject(new Error('Invalid association ID format'));
    }

    // SECURITY: Validate resource data
    if (!resource || typeof resource !== 'object' || !resource.title || !resource.body) {
      return Promise.reject(new Error('Invalid resource data: title and body are required'));
    }

    return AssociationsApi.post<IAssociationResource>(
      `${AssociationsApi.associationsApiAddress}${associationId}/resources`,
      resource
    ).then((res) => res.data.data);
  }

  static updateAssociationResource(associationId: string, resourceId: string, resource: {
    title: string;
    body: string;
    role?: string;
    activeFrom?: Date;
  }): Promise<IAssociationResource> {
    return AssociationsApi.put<IAssociationResource>(
      `${AssociationsApi.associationsApiAddress}${associationId}/resources/${resourceId}`,
      resource
    ).then((res) => res.data.data);
  }

  static endDateAssociationResource(associationId: string, resourceId: string, endDate?: Date): Promise<IAssociationResource> {
    return AssociationsApi.delete<IAssociationResource>(
      `${AssociationsApi.associationsApiAddress}${associationId}/resources/${resourceId}`,
      { endDate }
    ).then((res) => res.data.data);
  }
}

export default AssociationsApi;
